import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap, timeout } from 'rxjs/operators';

// হোমপেজের একটা সেকশনের সেটিং (অ্যাডমিন → হোমপেজ ডিজাইন থেকে বদলায়)
export interface HomeSection {
  id?: number;
  sectionKey: string;
  titleBn: string | null;
  titleEn: string | null;
  enabled: boolean;
  displayOrder: number;
  // প্রোডাক্ট সেকশনে: প্রথমে কয় সারি | পপুলার ক্যাটাগরিতে: সর্বোচ্চ কয়টা | বাকিতে ফাঁকা
  itemLimit: number | null;
}

// কোন সেকশনে কী ধরনের "আইটেম সংখ্যা" সেটিং আছে — অ্যাডমিন পেজ এটা দেখে ঘর দেখায়
export type LimitKind = 'rows' | 'count' | 'none';

export interface SectionMeta {
  key: string;
  icon: string;
  nameBn: string;
  nameEn: string;
  limit: LimitKind;
  // ডিফল্ট টাইটেল (ব্যাকএন্ড না থাকলেও সাইট ঠিকমতো চলবে)
  titleBn: string;
  titleEn: string;
  defaultLimit: number | null;
  // সাইটে টাইটেল দেখায় কি না (Hero / Features-এ টাইটেল নেই)
  hasTitle: boolean;
  // সরানো যায় না — সবসময় সবার উপরে
  pinnedTop?: boolean;
}

export const SECTION_META: SectionMeta[] = [
  { key: 'HERO',               icon: '🖼️', nameBn: 'মূল ব্যানার',        nameEn: 'Main banner',       limit: 'none',  titleBn: 'মূল ব্যানার',         titleEn: 'Main banner',        defaultLimit: null, hasTitle: false, pinnedTop: true },
  { key: 'FEATURES',           icon: '✨', nameBn: 'ফিচার বার',          nameEn: 'Features bar',      limit: 'none',  titleBn: 'ফিচার বার',           titleEn: 'Features bar',       defaultLimit: null, hasTitle: false },
  { key: 'POPULAR_CATEGORIES', icon: '🔥', nameBn: 'পপুলার ক্যাটাগরি',    nameEn: 'Popular categories',limit: 'count', titleBn: 'জনপ্রিয় ক্যাটাগরি',    titleEn: 'Popular Categories', defaultLimit: null, hasTitle: true },
  { key: 'HOT_DEALS',          icon: '💥', nameBn: 'হট ডিলস',            nameEn: 'Hot deals',         limit: 'rows',  titleBn: 'হট ডিলস',             titleEn: 'Hot Deals',          defaultLimit: 2,    hasTitle: true },
  { key: 'RECOMMENDED',        icon: '👍', nameBn: 'রেকমেন্ডেড',         nameEn: 'Recommended',       limit: 'rows',  titleBn: 'রেকমেন্ডেড',          titleEn: 'Recommended',        defaultLimit: 2,    hasTitle: true },
  { key: 'TRENDING',           icon: '📈', nameBn: 'ট্রেন্ডিং',           nameEn: 'Trending',          limit: 'rows',  titleBn: 'ট্রেন্ডিং',            titleEn: 'Trending',           defaultLimit: 2,    hasTitle: true },
  { key: 'SHOP_MORE',          icon: '🛍️', nameBn: 'শপ অ্যান্ড গেট মোর',  nameEn: 'Shop & Get More',   limit: 'none',  titleBn: 'শপ ও আরও পান',  titleEn: 'Shop & Get More',    defaultLimit: null, hasTitle: true },
  { key: 'POPULAR_BRANDS',     icon: '🏷️', nameBn: 'পপুলার ব্র্যান্ডস',   nameEn: 'Popular brands',    limit: 'none',  titleBn: 'জনপ্রিয় ব্র্যান্ড',   titleEn: 'Popular Brands',     defaultLimit: null, hasTitle: true }
];

export function defaultSections(): HomeSection[] {

  return SECTION_META.map((m, i) => ({
    sectionKey: m.key,
    titleBn: m.titleBn,
    titleEn: m.titleEn,
    enabled: true,
    displayOrder: i + 1,
    itemLimit: m.defaultLimit
  }));
}

@Injectable({ providedIn: 'root' })
export class HomepageLayoutService {

  readonly api =
    'https://superbangladesh-api-1.onrender.com/api/homepage-sections';

  private cache$?: Observable<HomeSection[]>;

  // সর্বশেষ লোডে সার্ভারের সাথে যোগাযোগ ব্যর্থ হয়ে ডিফল্ট ব্যবহার হয়েছে কি না
  lastLoadFailed = false;

  constructor(private http: HttpClient) {}

  // সার্ভার থেকে সেকশন লিস্ট। ব্যাকএন্ড এখনো আপডেট না হলে/এরর হলে
  // ডিফল্ট ক্রম ব্যবহার হয় — তাই হোমপেজ কখনো ভেঙে যাবে না।
  getSections(force = false): Observable<HomeSection[]> {

    if (force) {
      this.cache$ = undefined;
    }

    if (!this.cache$) {

      this.cache$ = this.http.get<HomeSection[]>(this.api).pipe(

        // সার্ভার (Render) ঘুমিয়ে থাকলে অনন্তকাল অপেক্ষা না করে ১০ সেকেন্ডে ছেড়ে দেয়
        timeout(10000),

        map(list => {
          this.lastLoadFailed = false;
          return this.merge(list);
        }),

        catchError(() => {
          this.cache$ = undefined;
          this.lastLoadFailed = true;
          return of(defaultSections());
        }),

        shareReplay({ bufferSize: 1, refCount: false })
      );
    }

    return this.cache$;
  }

  // সার্ভারে যে সেকশন নেই (নতুন যোগ হলে) সেগুলো ডিফল্ট দিয়ে ভরে দেয়
  private merge(list: HomeSection[] | null): HomeSection[] {

    const saved = Array.isArray(list) ? list : [];

    if (!saved.length) {
      return defaultSections();
    }

    const out = [...saved];

    for (const d of defaultSections()) {

      if (!out.some(s => s.sectionKey === d.sectionKey)) {

        out.push({
          ...d,
          displayOrder: 1000 + d.displayOrder
        });
      }
    }

    return this.sorted(out);
  }

  private sorted(list: HomeSection[]): HomeSection[] {

    return [...list].sort((a, b) =>
      (a.displayOrder - b.displayOrder) ||
      ((a.id ?? 0) - (b.id ?? 0))
    );
  }

  save(list: HomeSection[]): Observable<HomeSection[]> {

    const body = list.map((s, i) => ({
      sectionKey: s.sectionKey,
      titleBn: s.titleBn,
      titleEn: s.titleEn,
      enabled: s.enabled,
      displayOrder: i + 1,
      itemLimit: s.itemLimit
    }));

    return this.http.put<HomeSection[]>(this.api, body).pipe(
      map(res => this.merge(res)),
      tap(() => (this.cache$ = undefined))
    );
  }

  reset(): Observable<HomeSection[]> {

    return this.http.post<HomeSection[]>(`${this.api}/reset`, {}).pipe(
      map(res => this.merge(res)),
      tap(() => (this.cache$ = undefined))
    );
  }

  clear(): void {
    this.cache$ = undefined;
  }
}
